import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  LogOut,
  Download,
  Square,
  LayoutDashboard,
  Camera,
  Unlock,
  Shield,
  PartyPopper,
  Settings,
  Menu,
  X,
  Layout,
  RefreshCw,
  Trash2,
  Calendar,
  MessageSquare,
  CreditCard,
  Bell,
  Edit
} from 'lucide-react';
import axios from 'axios';
import moment from 'moment';
import { istDate, istTime, istDateTime, istDateTimeMed, isoToISTInputValue, istInputValueToISO } from './utils/istDate';
import SettingsPhotoshootSlots from './SettingsPhotoshootSlots';
import SettingsTerms from './SettingsTerms';
import SettingsPolicies from './SettingsPolicies';
import SettingsNotifications from './SettingsNotifications';
import SettingsBookingWindow from './SettingsBookingWindow';
import SettingsSiteBanner from './SettingsSiteBanner';
import SettingsPhotoshootPricing from './SettingsPhotoshootPricing';
import SettingsShootNotices from './SettingsShootNotices';
import LandingPageEditor from './LandingPageEditor';
import CalendarManager from './CalendarManager';
import BookingDetailsModal from './BookingDetailsModal';

// Helper function to get slot label from ID
const getSlotLabel = (slotId) => {
  const slotMap = {
    'A1': '06:30 AM – 09:30 AM (3hr)',
    'A2': '11:00 AM – 02:00 PM (3hr)',
    'A3': '03:30 PM – 06:30 PM (3hr)',
    'B1': '06:00 AM – 12:00 PM (6hr)',
    'B2': '01:00 PM – 07:00 PM (6hr)',
    'C1': '06:00 AM – 06:00 PM (12hr)'
  };
  return slotMap[slotId] || slotId;
};

// Helper component for slot badge
const SlotBadge = ({ slotId, className = '' }) => {
  const slotColors = {
    'A1': 'bg-blue-100 text-blue-800 border-blue-300',
    'A2': 'bg-blue-100 text-blue-800 border-blue-300',
    'A3': 'bg-blue-100 text-blue-800 border-blue-300',
    'B1': 'bg-purple-100 text-purple-800 border-purple-300',
    'B2': 'bg-purple-100 text-purple-800 border-purple-300',
    'C1': 'bg-amber-100 text-amber-800 border-amber-300'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${slotColors[slotId] || 'bg-gray-100 text-gray-800 border-gray-300'} ${className}`}>
      {slotId}
    </span>
  );
};

const SlotPicker = ({ selectedSlot, onSelect }) => {
  const slots = [
    { id: 'A1', label: '06:30 AM', duration: '3hr' },
    { id: 'A2', label: '11:00 AM', duration: '3hr' },
    { id: 'A3', label: '03:30 PM', duration: '3hr' },
    { id: 'B1', label: '06:00 AM', duration: '6hr' },
    { id: 'B2', label: '01:00 PM', duration: '6hr' },
    { id: 'C1', label: '06:00 AM', duration: '12hr' }
  ];

  return (
    <div className="grid grid-cols-3 gap-2">
      {slots.map(s => (
        <button
          key={s.id}
          type="button"
          onClick={() => onSelect(s.id)}
          className={`flex flex-col items-center justify-center p-2 border rounded transition-colors ${selectedSlot === s.id
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
        >
          <span className="font-bold text-sm">{s.id}</span>
          <span className="text-xs opacity-80">{s.label}</span>
          <span className="text-[10px] opacity-60">({s.duration})</span>
        </button>
      ))}
    </div>
  );
};

// Dynamic Slot Picker - fetches available slots based on selected date
const DynamicSlotPicker = ({ selectedDate, selectedSlot, onSelect }) => {
  const [availableSlots, setAvailableSlots] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const API_BASE_URL = '/api';

  const allSlotDetails = [
    { id: 'A1', label: '06:30 AM', duration: '3hr' },
    { id: 'A2', label: '11:00 AM', duration: '3hr' },
    { id: 'A3', label: '03:30 PM', duration: '3hr' },
    { id: 'B1', label: '06:00 AM', duration: '6hr' },
    { id: 'B2', label: '01:00 PM', duration: '6hr' },
    { id: 'C1', label: '06:00 AM', duration: '12hr' }
  ];

  React.useEffect(() => {
    if (!selectedDate) {
      setAvailableSlots([]);
      return;
    }

    const fetchAvailableSlots = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('admin_token');
        const formattedDate = moment(selectedDate).format('YYYY-MM-DD');

        const { data } = await axios.get(`${API_BASE_URL}/admin/available-slots`, {
          params: { date: formattedDate },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (data.success) {
          setAvailableSlots(data.available_slots);

          // Clear selection if currently selected slot is no longer available
          if (selectedSlot && !data.available_slots.includes(selectedSlot)) {
            onSelect(null);
          }
        }
      } catch (error) {
        console.error('Error fetching available slots:', error);
        setAvailableSlots([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableSlots();
  }, [selectedDate]);

  if (!selectedDate) {
    return (
      <div className="text-center text-gray-500 py-8 text-sm">
        Please select a date first
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center text-gray-500 py-8 text-sm">
        Loading available slots...
      </div>
    );
  }

  if (availableSlots.length === 0) {
    return (
      <div className="text-center text-red-500 py-8 text-sm">
        No slots available for this date
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2">
      {allSlotDetails.map(s => {
        const isAvailable = availableSlots.includes(s.id);
        const isSelected = selectedSlot === s.id;

        return (
          <button
            key={s.id}
            type="button"
            onClick={() => isAvailable && onSelect(s.id)}
            disabled={!isAvailable}
            className={`flex flex-col items-center justify-center p-2 border rounded transition-colors ${isSelected
              ? 'bg-blue-600 text-white border-blue-600'
              : isAvailable
                ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
          >
            <span className="font-bold text-sm">{s.id}</span>
            <span className="text-xs opacity-80">{s.label}</span>
            <span className="text-[10px] opacity-60">({s.duration})</span>
            {!isAvailable && <span className="text-[9px] mt-0.5 text-red-500">Booked</span>}
          </button>
        );
      })}
    </div>
  );
};


const SettingsEventQuote = () => {
  const [cfg, setCfg] = React.useState(null);
  const API_BASE_URL = '/api';
  React.useEffect(() => {
    const token = localStorage.getItem('admin_token');
    axios.get(`${API_BASE_URL}/admin/settings`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => {
        if (res.data.success) setCfg(res.data.settings.event_pricing);
      })
      .catch(() => {
        // Fallback defaults if settings not reachable
        setCfg({ basePrice: 5000, perGuestPrice: 200, housekeepingFee: 1000, deposit: 2500, decorMultipliers: { small: 1, medium: 1.3, large: 1.6 } });
      });
  }, []);
  if (!cfg) return <div className="text-gray-500">Loading…</div>;
  const save = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const { data } = await axios.get(`${API_BASE_URL}/admin/settings`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const settings = data.settings || {};
      settings.event_pricing = cfg;
      await axios.put(`${API_BASE_URL}/admin/settings`, settings, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      alert('Saved');
    } catch (e) { alert('Failed to save'); }
  };
  const reset = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.delete(`${API_BASE_URL}/admin/settings`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (res.data?.success) {
        setCfg(res.data.settings.event_pricing);
        alert('Reset to defaults');
      }
    } catch (e) { alert('Failed to reset'); }
  }
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">BASE FEE (₹)</label>
          <input
            type="number"
            value={cfg.basePrice}
            onChange={e => setCfg({ ...cfg, basePrice: Number(e.target.value) })}
            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">PER GUEST (₹)</label>
          <input
            type="number"
            value={cfg.perGuestPrice}
            onChange={e => setCfg({ ...cfg, perGuestPrice: Number(e.target.value) })}
            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">HOUSEKEEPING (₹)</label>
          <input
            type="number"
            value={cfg.housekeepingFee}
            onChange={e => setCfg({ ...cfg, housekeepingFee: Number(e.target.value) })}
            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">FULL PAYMENT (₹)</label>
          <input
            type="number"
            value={cfg.deposit}
            onChange={e => setCfg({ ...cfg, deposit: Number(e.target.value) })}
            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
          />
        </div>
      </div>

      <div className="pt-6 border-t border-gray-50">
        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4 px-1">Decor Multipliers</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {Object.entries(cfg.decorMultipliers).map(([k, v]) => (
            <div key={k} className="bg-gray-50/50 p-4 rounded-2xl border border-gray-100 flex flex-col gap-2">
              <span className="text-[10px] font-bold text-[#471F21] uppercase tracking-wider bg-[#471F21]/5 self-start px-2 py-0.5 rounded-full">{k}</span>
              <input
                type="number"
                step="0.1"
                value={v}
                onChange={e => setCfg({ ...cfg, decorMultipliers: { ...cfg.decorMultipliers, [k]: Number(e.target.value) } })}
                className="w-full bg-transparent border-none text-xl font-bold text-gray-800 focus:ring-0 p-0"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex gap-4 justify-end pt-6 border-t border-gray-50">
        <button
          onClick={reset}
          className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-400 text-sm font-bold hover:text-red-500 hover:bg-white transition-all"
        >
          Reset Defaults
        </button>
        <button
          onClick={save}
          className="px-8 py-2.5 rounded-xl text-white text-sm font-bold shadow-lg shadow-[#471F21]/20 hover:shadow-xl transition-all"
          style={{ background: '#471F21' }}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

const EventsTicketing = () => {
  const [events, setEvents] = React.useState([]);
  const [tickets, setTickets] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [orderSort, setOrderSort] = React.useState({ sort_by: 'purchased', sort_dir: 'desc' });
  const [loading, setLoading] = React.useState(true);
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [showEventForm, setShowEventForm] = React.useState(false);
  const [eventForm, setEventForm] = React.useState({
    id: '',
    name: '',
    description: '',
    cover_image_url: '',
    start_at: '',
    end_at: '',
    status: 'draft',
    // Ticket settings
    enable_vip: false,
    general_price: '',
    vip_price: '',
    general_quantity: '',
    vip_quantity: '',
    sales_start_at: '',
    sales_end_at: ''
  });
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [savingEvent, setSavingEvent] = React.useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadEvents = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/events`, { headers });
      if (data.success) setEvents(data.events || []);
    } finally { setLoading(false); }
  };

  const loadTickets = async (eventId) => {
    if (!eventId) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/events/${eventId}/tickets`, { headers });
      if (data.success) {
        setTickets(data.tickets || []);
      } else {
        console.error('Failed to load tickets:', data.message);
        setTickets([]);
      }
    } catch (e) {
      console.error('Error loading tickets:', e);
      setTickets([]);
    }
  };

  const loadOrders = async (eventId, sort = orderSort) => {
    if (!eventId) return;
    try {
      const qs = new URLSearchParams(sort).toString();
      const { data } = await axios.get(`${API_BASE_URL}/admin/events/${eventId}/orders?${qs}`, { headers });
      if (data.success) setOrders(data.orders || []);
    } catch (e) {
      console.error('Error loading orders:', e);
    }
  };

  // Sorting happens server-side, so a change means a refetch.
  React.useEffect(() => {
    if (selectedEvent) loadOrders(selectedEvent, orderSort);
  }, [orderSort]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => { loadEvents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Image upload function
  const handleImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post('/api/admin/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setEventForm(prev => ({ ...prev, cover_image_url: response.data.url }));
        setImagePreview(response.data.url);
        toast.success('Image uploaded successfully');
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveEvent = async () => {
    // Prevent multiple submissions
    if (savingEvent) return;

    try {
      setSavingEvent(true);

      // The datetime-local inputs hold IST wall-clock values (see formatDateForInput).
      // Convert them to UTC ISO for storage so the timestamps round-trip correctly.
      const startAtISO = istInputValueToISO(eventForm.start_at);
      const endAtISO = istInputValueToISO(eventForm.end_at);
      const salesStartISO = istInputValueToISO(eventForm.sales_start_at);
      const salesEndISO = istInputValueToISO(eventForm.sales_end_at);

      // First save the event
      const eventData = {
        id: eventForm.id,
        name: eventForm.name,
        description: eventForm.description,
        cover_image_url: eventForm.cover_image_url,
        start_at: startAtISO,
        end_at: endAtISO,
        status: eventForm.status
      };

      const { data: eventResult } = await axios.post(`${API_BASE_URL}/admin/events`, eventData, { headers });

      // Then create/update tickets
      if (eventResult.success) {
        const eventId = eventResult.event.id;

        // Look up existing tickets by name (there are only ever two: General
        // Admission and VIP Pass) so we can upsert them in place instead of
        // deleting and recreating. Deleting cascades to ticket_orders (FK ON
        // DELETE CASCADE) and would erase paid orders and reset quantity_sold
        // to 0 on every event edit.
        let existingGeneral = null;
        let existingVip = null;
        if (eventForm.id) {
          try {
            const { data: existingTicketsData } = await axios.get(`${API_BASE_URL}/admin/events/${eventId}/tickets`, { headers });
            if (existingTicketsData.success && existingTicketsData.tickets) {
              existingGeneral = existingTicketsData.tickets.find(t => t.name === 'General Admission') || null;
              existingVip = existingTicketsData.tickets.find(t => t.name === 'VIP Pass') || null;
            }
          } catch (fetchError) {
            console.error('Error fetching existing tickets:', fetchError);
          }
        }

        // General Admission: upsert in place if configured, delete only if it was removed.
        if (eventForm.general_price && eventForm.general_quantity) {
          await axios.post(`${API_BASE_URL}/admin/events/${eventId}/tickets`, {
            id: existingGeneral?.id,
            name: 'General Admission',
            price: parseInt(eventForm.general_price),
            quantity_total: parseInt(eventForm.general_quantity),
            sales_start_at: salesStartISO,
            sales_end_at: salesEndISO
          }, { headers });
        } else if (existingGeneral) {
          await axios.delete(`${API_BASE_URL}/admin/events/${eventId}/tickets/${existingGeneral.id}`, { headers });
        }

        // VIP Pass: upsert in place if enabled, delete only if it was disabled/removed.
        if (eventForm.enable_vip && eventForm.vip_price && eventForm.vip_quantity) {
          await axios.post(`${API_BASE_URL}/admin/events/${eventId}/tickets`, {
            id: existingVip?.id,
            name: 'VIP Pass',
            price: parseInt(eventForm.vip_price),
            quantity_total: parseInt(eventForm.vip_quantity),
            sales_start_at: salesStartISO,
            sales_end_at: salesEndISO
          }, { headers });
        } else if (existingVip) {
          await axios.delete(`${API_BASE_URL}/admin/events/${eventId}/tickets/${existingVip.id}`, { headers });
        }

        // Reload everything
        loadEvents();
        if (selectedEvent) {
          loadTickets(selectedEvent);
          loadOrders(selectedEvent);
        }
      }

      // Reset form
      setEventForm({
        id: '',
        name: '',
        description: '',
        cover_image_url: '',
        start_at: '',
        end_at: '',
        status: 'draft',
        enable_vip: false,
        general_price: '',
        vip_price: '',
        general_quantity: '',
        vip_quantity: '',
        sales_start_at: '',
        sales_end_at: ''
      });
      setImagePreview(null);
      setShowEventForm(false);
      toast.success(eventForm.id ? 'Event updated successfully!' : 'Event created successfully!');
    } catch (e) {
      toast.error('Error saving event: ' + (e.response?.data?.message || e.message));
    } finally {
      setSavingEvent(false);
    }
  };

  const editEvent = async (event) => {
    try {
      // Fetch tickets directly instead of relying on state
      const { data: ticketsData } = await axios.get(`${API_BASE_URL}/admin/events/${event.id}/tickets`, { headers });

      let generalTicket = null;
      let vipTicket = null;

      if (ticketsData.success && ticketsData.tickets) {
        generalTicket = ticketsData.tickets.find(t => t.name === 'General Admission');
        vipTicket = ticketsData.tickets.find(t => t.name === 'VIP Pass');
      }

      // Format dates for datetime-local inputs in IST wall-clock (converted back to UTC
      // on save via istInputValueToISO). Previously used UTC, so the admin edited in UTC.
      const formatDateForInput = (dateString) => isoToISTInputValue(dateString);

      setEventForm({
        ...event,
        start_at: formatDateForInput(event.start_at),
        end_at: formatDateForInput(event.end_at),
        enable_vip: !!vipTicket,
        general_price: generalTicket?.price?.toString() || '',
        vip_price: vipTicket?.price?.toString() || '',
        general_quantity: generalTicket?.quantity_total?.toString() || '',
        vip_quantity: vipTicket?.quantity_total?.toString() || '',
        sales_start_at: formatDateForInput(generalTicket?.sales_start_at || event.start_at),
        sales_end_at: formatDateForInput(generalTicket?.sales_end_at || event.end_at)
      });
      setImagePreview(event.cover_image_url || null);
      setShowEventForm(true);

      console.log('✅ Event loaded for editing:', {
        eventName: event.name,
        generalTicket: generalTicket ? `₹${generalTicket.price} x ${generalTicket.quantity_total}` : 'None',
        vipTicket: vipTicket ? `₹${vipTicket.price} x ${vipTicket.quantity_total}` : 'None'
      });
    } catch (error) {
      console.error('❌ Error loading event for editing:', error);
      toast.error('Error loading ticket data. Please try again.');

      // Still open the form with basic event data
      setEventForm({
        ...event,
        start_at: isoToISTInputValue(event.start_at),
        end_at: isoToISTInputValue(event.end_at),
        enable_vip: false,
        general_price: '',
        vip_price: '',
        general_quantity: '',
        vip_quantity: '',
        sales_start_at: '',
        sales_end_at: ''
      });
      setImagePreview(event.cover_image_url || null);
      setShowEventForm(true);
    }
  };

  const removeEvent = async (id) => {
    if (window.confirm('Delete this event and all its tickets?')) {
      await axios.delete(`${API_BASE_URL}/admin/events/${id}`, { headers });
      loadEvents();
      if (selectedEvent === id) {
        setSelectedEvent(null);
        setTickets([]);
        setOrders([]);
      }
    }
  };

  const cleanupDuplicateTickets = async (eventId) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/admin/events/${eventId}/tickets`, { headers });
      if (data.success && data.tickets) {
        const tickets = data.tickets;
        const generalTickets = tickets.filter(t => t.name === 'General Admission');
        const vipTickets = tickets.filter(t => t.name === 'VIP Pass');

        // Keep only the first General Admission and VIP ticket, delete the rest
        const ticketsToDelete = [];

        if (generalTickets.length > 1) {
          ticketsToDelete.push(...generalTickets.slice(1));
        }

        if (vipTickets.length > 1) {
          ticketsToDelete.push(...vipTickets.slice(1));
        }

        for (const ticket of ticketsToDelete) {
          await axios.delete(`${API_BASE_URL}/admin/events/${eventId}/tickets/${ticket.id}`, { headers });
        }

        if (ticketsToDelete.length > 0) {
          alert(`Cleaned up ${ticketsToDelete.length} duplicate tickets`);
          loadTickets(eventId);
        } else {
          alert('No duplicate tickets found');
        }
      }
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      alert('Error cleaning up duplicates');
    }
  };

  const selectEvent = (eventId) => {
    setSelectedEvent(eventId);
    loadTickets(eventId);
    loadOrders(eventId);
  };

  const createNewEvent = () => {
    setEventForm({
      id: '',
      name: '',
      description: '',
      cover_image_url: '',
      start_at: '',
      end_at: '',
      status: 'draft',
      enable_vip: false,
      general_price: '',
      vip_price: '',
      general_quantity: '',
      vip_quantity: '',
      sales_start_at: '',
      sales_end_at: ''
    });
    setImagePreview(null);
    setShowEventForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{ color: '#471F21' }}>Events Management</h2>
        <button
          onClick={createNewEvent}
          className="px-6 py-3 rounded-lg text-white font-semibold"
          style={{ backgroundColor: '#471F21' }}
        >
          + Create New Event
        </button>
      </div>

      {/* Event Form Modal */}
      {showEventForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold" style={{ color: '#471F21' }}>
                {eventForm.id ? 'Edit Event' : 'Create New Event'}
              </h3>
              <button
                onClick={() => setShowEventForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Event Details */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg" style={{ color: '#471F21' }}>Event Details</h4>
                <input
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Event Name"
                  value={eventForm.name}
                  onChange={e => setEventForm({ ...eventForm, name: e.target.value })}
                />
                <textarea
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Description"
                  rows="3"
                  value={eventForm.description}
                  onChange={e => setEventForm({ ...eventForm, description: e.target.value })}
                />
                {/* Image Upload Section */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Event Cover Image
                  </label>

                  {/* Image Preview */}
                  {(imagePreview || eventForm.cover_image_url) && (
                    <div className="relative">
                      <img
                        src={imagePreview || eventForm.cover_image_url}
                        alt="Event preview"
                        className="w-full h-48 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setEventForm(prev => ({ ...prev, cover_image_url: '' }));
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex items-center space-x-3">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files[0])}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      <div className={`w-full py-2 px-4 border-2 border-dashed rounded-lg text-center transition-colors ${uploadingImage
                        ? 'border-gray-300 bg-gray-50'
                        : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                        }`}>
                        {uploadingImage ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            <span className="text-sm text-gray-600">Uploading...</span>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-600">
                            {imagePreview || eventForm.cover_image_url ? 'Change Image' : 'Upload Image'}
                          </div>
                        )}
                      </div>
                    </label>
                  </div>

                  {/* URL Input as fallback */}
                  <div className="text-xs text-gray-500">Or paste image URL:</div>
                  <input
                    className="border rounded px-3 py-2 w-full text-sm"
                    placeholder="https://example.com/image.jpg"
                    value={eventForm.cover_image_url}
                    onChange={e => {
                      setEventForm({ ...eventForm, cover_image_url: e.target.value });
                      setImagePreview(e.target.value || null);
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-600">Start Date & Time</label>
                    <input
                      type="datetime-local"
                      className="border rounded px-3 py-2 w-full"
                      value={eventForm.start_at}
                      onChange={e => setEventForm({ ...eventForm, start_at: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">End Date & Time</label>
                    <input
                      type="datetime-local"
                      className="border rounded px-3 py-2 w-full"
                      value={eventForm.end_at}
                      onChange={e => setEventForm({ ...eventForm, end_at: e.target.value })}
                    />
                  </div>
                </div>
                <select
                  className="border rounded px-3 py-2 w-full"
                  value={eventForm.status}
                  onChange={e => setEventForm({ ...eventForm, status: e.target.value })}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Ticket Settings */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg" style={{ color: '#471F21' }}>Ticket Settings</h4>

                {/* General Tickets */}
                <div className="border rounded p-4">
                  <h5 className="font-medium mb-3">General Admission</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-gray-600">Price (₹)</label>
                      <input
                        type="number"
                        className="border rounded px-3 py-2 w-full"
                        placeholder="500"
                        value={eventForm.general_price}
                        onChange={e => setEventForm({ ...eventForm, general_price: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Quantity</label>
                      <input
                        type="number"
                        className="border rounded px-3 py-2 w-full"
                        placeholder="50"
                        value={eventForm.general_quantity}
                        onChange={e => setEventForm({ ...eventForm, general_quantity: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* VIP Tickets */}
                <div className="border rounded p-4">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="enable_vip"
                      className="mr-2"
                      checked={eventForm.enable_vip}
                      onChange={e => setEventForm({ ...eventForm, enable_vip: e.target.checked })}
                    />
                    <label htmlFor="enable_vip" className="font-medium">Enable VIP Tickets</label>
                  </div>
                  {eventForm.enable_vip && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-gray-600">VIP Price (₹)</label>
                        <input
                          type="number"
                          className="border rounded px-3 py-2 w-full"
                          placeholder="1000"
                          value={eventForm.vip_price}
                          onChange={e => setEventForm({ ...eventForm, vip_price: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">VIP Quantity</label>
                        <input
                          type="number"
                          className="border rounded px-3 py-2 w-full"
                          placeholder="20"
                          value={eventForm.vip_quantity}
                          onChange={e => setEventForm({ ...eventForm, vip_quantity: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Sales Window */}
                <div className="border rounded p-4">
                  <h5 className="font-medium mb-3">Sales Window</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-gray-600">Sales Start</label>
                      <input
                        type="datetime-local"
                        className="border rounded px-3 py-2 w-full"
                        value={eventForm.sales_start_at}
                        onChange={e => setEventForm({ ...eventForm, sales_start_at: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Sales End</label>
                      <input
                        type="datetime-local"
                        className="border rounded px-3 py-2 w-full"
                        value={eventForm.sales_end_at}
                        onChange={e => setEventForm({ ...eventForm, sales_end_at: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={saveEvent}
                disabled={savingEvent}
                className="px-6 py-3 rounded text-white font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#471F21' }}
              >
                {savingEvent && (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {savingEvent ? 'Saving...' : (eventForm.id ? 'Update Event' : 'Create Event')}
              </button>
              <button
                onClick={() => setShowEventForm(false)}
                className="px-6 py-3 rounded text-gray-600 border border-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Events List */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#471F21' }}>All Events</h3>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {events.map(e => (
                <div
                  key={e.id}
                  className={`border rounded p-4 cursor-pointer transition-colors ${selectedEvent === e.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  onClick={() => selectEvent(e.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold text-lg">{e.name}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {istDateTimeMed(e.start_at)} – {istDateTimeMed(e.end_at)}
                      </div>
                      <div className="text-xs mt-2" style={{ color: e.status === 'published' ? '#10B981' : e.status === 'draft' ? '#F59E0B' : '#EF4444' }}>
                        {e.status.toUpperCase()}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={(event) => { event.stopPropagation(); editEvent(e); }}
                        className="text-blue-600 text-sm hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(event) => { event.stopPropagation(); removeEvent(e.id); }}
                        className="text-red-600 text-sm hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {events.length === 0 && <div className="text-gray-600 text-center py-8">No events yet. Create your first event!</div>}
            </div>
          )}
        </div>

        {/* Event Details Panel */}
        <div className="bg-white rounded-lg shadow p-6">
          {!selectedEvent ? (
            <div className="text-center py-8 text-gray-600">
              <div className="text-4xl mb-4">📅</div>
              <div>Select an event to view details, tickets, and orders</div>
            </div>
          ) : (
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#471F21' }}>
                {events.find(e => e.id === selectedEvent)?.name} - Details
              </h3>

              {/* Tickets Section */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold" style={{ color: '#471F21' }}>Ticket Tiers</h4>
                  {tickets.length > 0 && (
                    <button
                      onClick={() => cleanupDuplicateTickets(selectedEvent)}
                      className="text-xs px-3 py-1 rounded text-red-600 border border-red-300 hover:bg-red-50"
                    >
                      Clean Duplicates
                    </button>
                  )}
                </div>
                {tickets.length === 0 ? (
                  <div className="text-gray-600 text-sm">No tickets configured for this event</div>
                ) : (
                  <div className="space-y-2">
                    {tickets.map(t => (
                      <div key={t.id} className="border rounded p-3 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium">{t.name}</div>
                            <div className="text-sm text-gray-600">₹{t.price.toLocaleString()} • {t.quantity_sold || 0}/{t.quantity_total} sold</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500">
                              {Math.round(((t.quantity_sold || 0) / t.quantity_total) * 100)}% sold
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Orders Section */}
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h4 className="font-semibold" style={{ color: '#471F21' }}>Orders</h4>
                  <div className="flex items-center gap-2">
                    <select
                      value={orderSort.sort_by}
                      onChange={(e) => setOrderSort({ ...orderSort, sort_by: e.target.value })}
                      className="text-xs border rounded-lg px-2 py-1 bg-white"
                    >
                      <option value="purchased">Purchase date</option>
                      <option value="buyer">Buyer</option>
                      <option value="amount">Amount</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setOrderSort({ ...orderSort, sort_dir: orderSort.sort_dir === 'asc' ? 'desc' : 'asc' })}
                      title={orderSort.sort_dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                      className="text-xs border rounded-lg px-2 py-1 hover:bg-gray-50"
                    >
                      {orderSort.sort_dir === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>
                </div>
                {orders.length === 0 ? (
                  <div className="text-gray-600 text-sm">No orders yet</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {orders.map(o => (
                      <div key={o.id} className="border rounded p-3 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-sm">{o.buyer_name}</div>
                            <div className="text-xs text-gray-600">{tickets.find(t => t.id === o.ticket_id)?.name || 'Unknown'}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium">₹{o.amount_paid.toLocaleString()}</div>
                            <div className="text-xs text-gray-500">{o.quantity} ticket{o.quantity > 1 ? 's' : ''}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Use relative URL so it works with any deployment
const API_BASE_URL = '/api';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    booking_type: '',
    start_date: '',
    end_date: '',
    // Sorting and per-view filtering happen on the server. They used to run in the
    // browser over whatever page happened to be loaded, so "Highest Price" meant
    // highest of the visible handful rather than of the whole result set.
    sort_by: 'booked',
    sort_dir: 'desc',
    page: 1,
    limit: 25
  });
  const [pagination, setPagination] = useState({});
  const [blockedSlotsFilters, setBlockedSlotsFilters] = useState({
    start_date: '',
    end_date: '',
    slot_id: '',
    sort_by: 'date',
    sort_dir: 'desc',
    page: 1,
    limit: 25
  });
  const [blockedSlotsPagination, setBlockedSlotsPagination] = useState({});
  const [selectedSlotIds, setSelectedSlotIds] = useState(new Set());
  const [showBlockSlot, setShowBlockSlot] = useState(false);
  const [blockSlotType, setBlockSlotType] = useState('slot'); // 'slot' or 'range'
  const [blockSlotDate, setBlockSlotDate] = useState(null);
  const [blockSlotStartDate, setBlockSlotStartDate] = useState(null);
  const [blockSlotEndDate, setBlockSlotEndDate] = useState(null);
  const [blockSlotId, setBlockSlotId] = useState('');
  const [blockSlotReason, setBlockSlotReason] = useState('');
  const [showFreeBooking, setShowFreeBooking] = useState(false);
  const [freeBookingData, setFreeBookingData] = useState({
    user_name: '',
    user_email: '',
    phone: '',
    booking_type: 'photoshoot',
    date: '',     // YYYY-MM-DD
    slot_id: '',  // A1, A2, etc.
    shoot_type: '',
    event_type: '',
    guest_count: '',
    decor_size: '',
    special_requirements: ''
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // dashboard, events, photoshoots, blocked-slots
  const [settingsTab, setSettingsTab] = useState('general'); // general, photoshoot, event, notifications, policies
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [quickFilter, setQuickFilter] = useState([]); // Array for multi-select: ['blocked', '3hr', 'cancelled', etc.]
  // Blocked slots shown inline on the Dashboard only. Kept out of `bookings` so they
  // cannot disturb the server's ordering or pagination of the other views.
  const [blockedOverlay, setBlockedOverlay] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportRange, setExportRange] = useState({ start_date: '', end_date: '', status: '' });

  // Sidebar navigation items
  const navigationItems = [
    { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
    { id: 'calendar', name: 'Calendar Manager', icon: Calendar },
    { id: 'photoshoots', name: 'Photoshoots', icon: Camera },
    { id: 'events', name: 'Events (Legacy)', icon: PartyPopper },
    { id: 'events-ticketing', name: 'Events (Ticketing)', icon: PartyPopper },
    { id: 'completed', name: 'Completed', icon: CheckCircle },
    { id: 'cancelled', name: 'Cancelled', icon: XCircle },
    { id: 'settings', name: 'Settings', icon: Settings },
    { id: 'website', name: 'Website Content', icon: Layout },
    { id: 'blocked-slots', name: 'Blocked Slots', icon: Shield }
  ];

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken();
    }
  }, []);

  // Each list view is really a server-side filter over the same bookings endpoint.
  // Holding it here (rather than filtering the loaded page in the browser) is what lets
  // Completed and Cancelled paginate over every matching row instead of just the rows
  // that happened to land on the current page.
  const VIEW_FILTERS = {
    photoshoots: { booking_type: 'photoshoot', status_not: 'completed' },
    events: { booking_type: 'event', status_not: 'completed' },
    completed: { status: 'completed' },
    cancelled: { status: 'cancelled' },
  };
  // The manual status/type dropdowns only render on the Dashboard; everywhere else the
  // view supplies these filters itself.
  const viewFilter = VIEW_FILTERS[currentView] || {};

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadBookings();
      loadStats();
    }
  }, [isAuthenticated, filters, currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switching view changes the server query, so restart at page 1.
  useEffect(() => {
    setFilters(f => (f.page === 1 ? f : { ...f, page: 1 }));
  }, [currentView]);

  useEffect(() => {
    if (isAuthenticated && currentView === 'blocked-slots') {
      loadBlockedSlots();
    }
  }, [isAuthenticated, blockedSlotsFilters, currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyToken = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email: formData.get('email'),
        password: formData.get('password')
      });

      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        setIsAuthenticated(true);
        toast.success('Login successful');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const loadBookings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();

      // The active view's filters win over the manual dropdowns — on Completed, say, the
      // view *is* status=completed and the dropdown is hidden.
      const query = { ...filters, ...viewFilter };
      Object.entries(query).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      // Blocked slots are a Dashboard-only overlay and are fetched unpaginated for the
      // current date window — high enough to cover them all rather than silently
      // truncating the overlay to a page.
      const blockedSlotsParams = new URLSearchParams();
      blockedSlotsParams.append('limit', '1000');
      if (filters.start_date) blockedSlotsParams.append('start_date', filters.start_date);
      if (filters.end_date) blockedSlotsParams.append('end_date', filters.end_date);

      const [bookingsResponse, blockedSlotsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/bookings?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/admin/blocked-slots?${blockedSlotsParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (bookingsResponse.data.success) {
        setBookings(bookingsResponse.data.bookings);
        setPagination(bookingsResponse.data.pagination);
      }

      if (blockedSlotsResponse.data.success && blockedSlotsResponse.data.blocked_slots) {
        // Shaped like bookings so the Dashboard table can render them in the same rows.
        setBlockedOverlay(blockedSlotsResponse.data.blocked_slots.map(slot => ({
          id: `blocked-${slot.id}`,
          _isBlockedSlot: true,
          _blockedSlotId: slot.id,
          user_name: 'BLOCKED',
          user_email: slot.reason || 'Manual block',
          phone: '-',
          booking_type: 'blocked',
          slot_id: slot.slot_id,
          slot_time: slot.slot_time,
          created_at: slot.created_at,
          payment_status: 'blocked',
          amount: 0,
          total_amount: 0,
          deposit: 0
        })));
      }
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      toast.error('Failed to load statistics');
    }
  };

  const loadBlockedSlots = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();

      Object.entries(blockedSlotsFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API_BASE_URL}/admin/blocked-slots?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setBlockedSlots(response.data.blocked_slots);
        setBlockedSlotsPagination(response.data.pagination);
        setSelectedSlotIds(new Set());
      }
    } catch (error) {
      toast.error('Failed to load blocked slots');
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.patch(
        `${API_BASE_URL}/admin/bookings/${bookingId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Booking status updated');
        loadBookings();
      }
    } catch (error) {
      toast.error('Failed to update booking status');
    }
  };

  const [resendingId, setResendingId] = useState(null);
  const resendWhatsApp = async (booking) => {
    if (!booking.phone) { toast.error('This booking has no phone number.'); return; }
    if (!window.confirm(`Resend the confirmation WhatsApp to ${booking.user_name} (${booking.phone})?`)) return;
    try {
      setResendingId(booking.id);
      const token = localStorage.getItem('admin_token');
      const { data } = await axios.post(
        `${API_BASE_URL}/admin/bookings/${booking.id}/resend-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        toast.success(data.message || 'WhatsApp resent');
      } else {
        toast.error(data.error || 'Failed to resend WhatsApp');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to resend WhatsApp');
    } finally {
      setResendingId(null);
    }
  };

  const [caretakerResendId, setCaretakerResendId] = useState(null);
  const resendCaretaker = async (booking) => {
    const kind = booking.payment_status === 'cancelled' ? 'cancellation' : 'booking';
    if (!window.confirm(`Resend the caretaker's ${kind} WhatsApp alert for ${booking.user_name}?`)) return;
    try {
      setCaretakerResendId(booking.id);
      const token = localStorage.getItem('admin_token');
      const { data } = await axios.post(
        `${API_BASE_URL}/admin/bookings/${booking.id}/resend-caretaker`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        toast.success(data.message || 'Caretaker alert resent');
      } else {
        toast.error(data.error || 'Failed to resend caretaker alert');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to resend caretaker alert');
    } finally {
      setCaretakerResendId(null);
    }
  };

  const [recheckingId, setRecheckingId] = useState(null);
  const recheckPayment = async (booking) => {
    if (!window.confirm(`Re-check this payment with PayU for ${booking.user_name}? If PayU reports it paid, the booking will be confirmed and the customer notified.`)) return;
    try {
      setRecheckingId(booking.id);
      const token = localStorage.getItem('admin_token');
      const { data } = await axios.post(
        `${API_BASE_URL}/admin/bookings/${booking.id}/recheck-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.status === 'confirmed') {
        toast.success(data.message || 'Payment verified — booking confirmed.');
        loadBookings();
      } else if (data.status === 'failed') {
        toast(data.message || 'PayU confirms this payment failed.', { icon: 'ℹ️' });
      } else {
        toast(data.message || 'Could not verify this payment.', { icon: '⚠️' });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to re-check payment');
    } finally {
      setRecheckingId(null);
    }
  };

  const blockSlot = async (e) => {
    e.preventDefault();

    if (blockSlotType === 'slot') {
      if (!blockSlotDate || !blockSlotId) {
        toast.error('Please select a date and slot');
        return;
      }
    } else {
      if (!blockSlotStartDate || !blockSlotEndDate) {
        toast.error('Please select start and end dates');
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');

      const payload = {
        type: blockSlotType,
        reason: blockSlotReason
      };

      if (blockSlotType === 'slot') {
        // Use moment to format date correctly without timezone issues
        payload.date = moment(blockSlotDate).format('YYYY-MM-DD');
        payload.slot_id = blockSlotId;
      } else {
        payload.start_date = moment(blockSlotStartDate).format('YYYY-MM-DD');
        payload.end_date = moment(blockSlotEndDate).format('YYYY-MM-DD');
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/block-slot`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Slot blocked successfully');
        setShowBlockSlot(false);
        // Reset state
        setBlockSlotDate(null);
        setBlockSlotId('');
        setBlockSlotStartDate(null);
        setBlockSlotEndDate(null);
        setBlockSlotReason('');
        loadBlockedSlots();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to block slot');
    } finally {
      setLoading(false);
    }
  };

  const unblockSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to unblock this slot?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.delete(
        `${API_BASE_URL}/admin/block-slot/${slotId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Slot unblocked successfully');
        loadBlockedSlots();
      }
    } catch (error) {
      toast.error('Failed to unblock slot');
    } finally {
      setLoading(false);
    }
  };

  const bulkUnblockSlots = async () => {
    if (selectedSlotIds.size === 0) return;
    if (!window.confirm(`Unblock ${selectedSlotIds.size} selected slot(s)?`)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.delete(
        `${API_BASE_URL}/admin/block-slots/bulk`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { ids: Array.from(selectedSlotIds) }
        }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedSlotIds(new Set());
        loadBlockedSlots();
      } else {
        toast.error(response.data.message || 'Failed to unblock slots');
      }
    } catch (error) {
      toast.error('Failed to unblock slots');
    } finally {
      setLoading(false);
    }
  };

  // Today in IST, regardless of the admin's device timezone — the backend filters on
  // the IST calendar date of the slot, so presets have to agree with that.
  const istToday = () => moment().utcOffset(330);

  const exportPresets = [
    {
      label: 'Upcoming',
      hint: 'Day before yesterday onwards',
      range: () => ({ start_date: istToday().subtract(2, 'days').format('YYYY-MM-DD'), end_date: '' })
    },
    {
      label: 'This month',
      hint: istToday().format('MMMM YYYY'),
      range: () => ({
        start_date: istToday().startOf('month').format('YYYY-MM-DD'),
        end_date: istToday().endOf('month').format('YYYY-MM-DD')
      })
    },
    {
      label: 'Next 30 days',
      hint: 'From today',
      range: () => ({
        start_date: istToday().format('YYYY-MM-DD'),
        end_date: istToday().add(30, 'days').format('YYYY-MM-DD')
      })
    },
    {
      label: 'All time',
      hint: 'Every booking on record',
      range: () => ({ start_date: '', end_date: '' })
    }
  ];

  const openExportModal = () => {
    // Seed from whatever the dashboard is already filtered to, so "Export" with a
    // range already picked does the obvious thing.
    setExportRange({
      start_date: filters.start_date || '',
      end_date: filters.end_date || '',
      status: filters.status || ''
    });
    setShowExportModal(true);
  };

  // Server-side CSV export. Deliberately NOT built from the `bookings` state — that
  // holds only the current page (6 rows), so a client-side build would silently
  // export a fraction of the range.
  const exportBookings = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (exportRange.start_date) params.append('start_date', exportRange.start_date);
      if (exportRange.end_date) params.append('end_date', exportRange.end_date);
      if (exportRange.status) params.append('status', exportRange.status);

      const response = await axios.get(
        `${API_BASE_URL}/admin/bookings/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );

      const match = /filename="?([^"]+)"?/.exec(response.headers['content-disposition'] || '');
      const filename = match ? match[1] : `bookings-${istToday().format('YYYY-MM-DD')}.csv`;

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setShowExportModal(false);
      toast.success('Export downloaded');
    } catch (error) {
      // responseType 'blob' means even error bodies arrive as a Blob, so the usual
      // error.response.data.message is unreadable without unwrapping it first.
      let message = 'Failed to export bookings';
      try {
        if (error.response?.data instanceof Blob) {
          const parsed = JSON.parse(await error.response.data.text());
          message = parsed.errors?.[0]?.message || parsed.message || message;
        }
      } catch (_) { /* fall back to the generic message */ }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const createFreeBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_BASE_URL}/admin/create-free-booking`,
        freeBookingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Free booking created successfully');
        setShowFreeBooking(false);
        setFreeBookingData({
          user_name: '',
          user_email: '',
          phone: '',
          booking_type: 'photoshoot',
          date: '',
          slot_id: '',
          shoot_type: '',
          event_type: '',
          guest_count: '',
          decor_size: '',
          special_requirements: ''
        });
        loadBookings();
        loadStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create free booking');
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      console.log('🔄 Cancelling booking:', cancelBookingId);
      console.log('📝 Reason:', cancelReason);

      const response = await axios.delete(
        `${API_BASE_URL}/admin/bookings/${cancelBookingId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          data: { reason: cancelReason }
        }
      );

      console.log('✅ Cancel response:', response.data);

      if (response.data.success) {
        toast.success('Booking cancelled successfully');
        setShowCancelModal(false);
        setCancelBookingId(null);
        setCancelReason('');
        loadBookings();
        loadStats();
      } else {
        toast.error(response.data.message || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('❌ Cancel error:', error);
      console.error('❌ Error response:', error.response?.data);
      toast.error(error.response?.data?.message || error.message || 'Failed to cancel booking');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('⚠️ Are you sure you want to PERMANENTLY DELETE this booking?\n\nThis action cannot be undone and NO notification will be sent to the user.')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      console.log('🗑️ Deleting booking:', bookingId);

      const response = await axios.delete(
        `${API_BASE_URL}/admin/bookings/${bookingId}/hard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Booking deleted permanently');
        loadBookings();
        loadStats();
        // If the deleted booking was selected, close details
        if (selectedBooking?.id === bookingId) {
          setShowBookingDetails(false);
          setSelectedBooking(null);
        }
      } else {
        toast.error(response.data.message || 'Failed to delete booking');
      }
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete booking');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockSlot = async (blockedSlotId) => {
    if (!window.confirm('Are you sure you want to UNBLOCK this slot?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.delete(
        `${API_BASE_URL}/admin/block-slot/${blockedSlotId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Slot unblocked successfully');
        loadBookings();
        loadStats();
        if (selectedBooking?._blockedSlotId === blockedSlotId) {
          setShowBookingDetails(false);
          setSelectedBooking(null);
        }
      } else {
        toast.error(response.data.message || 'Failed to unblock slot');
      }
    } catch (error) {
      console.error('Error unblocking slot:', error);
      toast.error('Internal server error');
    } finally {
      setLoading(false);
    }
  };

  const handleFreeBookingChange = (e) => {
    const { name, value } = e.target;
    setFreeBookingData(prev => ({ ...prev, [name]: value }));
  };

  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetails(true);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <h1 className="text-2xl font-bold text-center mb-6">Admin Login</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                name="password"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Value a row sorts by, mirroring the SQL in db/queries/bookings.sql. Only used to slot
  // the Dashboard's blocked-slot overlay into the server-ordered page — the bookings
  // themselves already arrive in the right order.
  const sortValue = (b, key) => {
    switch (key) {
      case 'appointment': return b.date || (b.slot_time ? moment(b.slot_time).utcOffset(330).format('YYYY-MM-DD') : '');
      case 'payment': return b.payment_completed_at ? moment(b.payment_completed_at).utcOffset(330).format('YYYY-MM-DD HH:mm') : '';
      case 'amount': return String(b.total_amount || b.amount || 0).padStart(12, '0');
      case 'name': return (b.user_name || '').toLowerCase();
      case 'booked':
      default: return b.created_at ? moment(b.created_at).utcOffset(330).format('YYYY-MM-DD HH:mm') : '';
    }
  };

  // Blank sorts last in both directions, matching the query's NULLS LAST.
  const compareBySort = (a, b) => {
    const va = sortValue(a, filters.sort_by), vb = sortValue(b, filters.sort_by);
    if (va === vb) return 0;
    if (va === '') return 1;
    if (vb === '') return -1;
    return filters.sort_dir === 'asc' ? (va < vb ? -1 : 1) : (va > vb ? -1 : 1);
  };

  // Rows for the current view. The server has already filtered and ordered them; only the
  // Dashboard adds the blocked-slot overlay, interleaved by the chosen sort.
  const getFilteredBookings = () => {
    if (currentView !== 'dashboard' || blockedOverlay.length === 0) return bookings;
    return [...bookings, ...blockedOverlay].sort(compareBySort);
  };

  const filteredBookings = getFilteredBookings();

  const SORT_OPTIONS = [
    { value: 'appointment', label: 'Appointment date' },
    { value: 'payment', label: 'Payment date' },
    { value: 'booked', label: 'Booked on' },
    { value: 'amount', label: 'Amount' },
    { value: 'name', label: 'Guest name' },
  ];

  // Sort + page-size controls, shared by every booking list view.
  const ListControls = () => (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sort by</span>
        <select
          className="text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#471F21]/20"
          value={filters.sort_by}
          onChange={(e) => setFilters({ ...filters, sort_by: e.target.value, page: 1 })}
        >
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setFilters({ ...filters, sort_dir: filters.sort_dir === 'asc' ? 'desc' : 'asc', page: 1 })}
          title={filters.sort_dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
          className="text-sm border rounded-lg px-2.5 py-1.5 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#471F21]/20"
        >
          {filters.sort_dir === 'asc' ? '↑' : '↓'}
        </button>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Per page</span>
        <select
          className="text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#471F21]/20"
          value={filters.limit}
          onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value, 10), page: 1 })}
        >
          {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );

  // Previous/Next with the server's filtered totals.
  const Pager = () => (
    pagination.total > filters.limit ? (
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Page {pagination.page || 1} of {pagination.pages || 1}
          <span className="text-gray-400"> · {pagination.total} record{pagination.total === 1 ? '' : 's'}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilters({ ...filters, page: Math.max(1, (filters.page || 1) - 1) })}
            disabled={(filters.page || 1) === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => setFilters({ ...filters, page: Math.min(pagination.pages || 1, (filters.page || 1) + 1) })}
            disabled={(filters.page || 1) >= (pagination.pages || 1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    ) : null
  );

  return (
    <div className="flex h-screen" style={{ backgroundColor: '#F4ECDE' }}>
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white shadow-lg transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-6 border-b" style={{ borderColor: '#F4ECDE' }}>
          {sidebarOpen ? (
            <div>
              <h1 className="text-xl font-bold" style={{ color: '#471F21' }}>Thaathayya Illu</h1>
              <p className="text-xs text-gray-500 mt-1">Admin Panel</p>
            </div>
          ) : (
            <div className="text-center">
              <span className="text-2xl font-bold" style={{ color: '#471F21' }}>T</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentView(item.id)}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-all ${isActive
                  ? 'text-white shadow-md'
                  : 'text-gray-700 hover:bg-gray-100'
                  }`}
                style={isActive ? { backgroundColor: '#471F21' } : {}}
              >
                <Icon className="w-5 h-5" />
                {sidebarOpen && <span className="ml-3 font-medium">{item.name}</span>}
              </button>
            );
          })}
        </nav>

        {/* Logout & Toggle */}
        <div className="p-4 border-t" style={{ borderColor: '#F4ECDE' }}>
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3 font-medium">Logout</span>}
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center px-4 py-2 mt-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">

          {/* Dashboard View */}
          {currentView === 'dashboard' && (
            <div>
              <h2 className="text-2xl font-semibold mb-8 text-gray-800">Dashboard Overview</h2>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* Total Bookings */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Total Bookings</p>
                      <p className="text-3xl font-bold text-blue-900">{stats.total_bookings || 0}</p>
                      <p className="text-xs text-blue-700 mt-2">
                        {stats.bookings_by_type?.photoshoot || 0} Photoshoots · {stats.bookings_by_type?.event || 0} Events
                      </p>
                    </div>
                    <div className="bg-blue-200 p-2.5 rounded-lg">
                      <Users className="w-5 h-5 text-blue-700" />
                    </div>
                  </div>
                </div>

                {/* Active Bookings */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-1">Active</p>
                      <p className="text-3xl font-bold text-purple-900">{stats.active_bookings || 0}</p>
                      <p className="text-xs text-purple-700 mt-2">Confirmed & Upcoming</p>
                    </div>
                    <div className="bg-purple-200 p-2.5 rounded-lg">
                      <Calendar className="w-5 h-5 text-purple-700" />
                    </div>
                  </div>
                </div>

                {/* Completed */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Completed</p>
                      <p className="text-3xl font-bold text-green-900">{stats.completed_bookings || 0}</p>
                      <p className="text-xs text-green-700 mt-2">
                        Avg: ₹{Math.round((stats.revenue?.completed || 0) / (stats.completed_bookings || 1)).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-green-200 p-2.5 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-700" />
                    </div>
                  </div>
                </div>

                {/* Revenue */}
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-emerald-900">
                        ₹{((stats.revenue?.total || 0) / 1000).toFixed(0)}k
                      </p>
                      <div className="flex gap-2 text-xs mt-2">
                        <span className="text-emerald-700">₹{((stats.revenue?.completed || 0) / 1000).toFixed(0)}k collected</span>
                      </div>
                    </div>
                    <div className="bg-emerald-200 p-2.5 rounded-lg">
                      <DollarSign className="w-5 h-5 text-emerald-700" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts and Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {/* Booking Type Distribution */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Booking Distribution</h3>
                  <div className="space-y-5">
                    {/* Photoshoots */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Photoshoots</span>
                        <span className="text-sm font-bold text-gray-900">{stats.bookings_by_type?.photoshoot || 0}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div
                          className="h-2.5 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500"
                          style={{
                            width: `${((stats.bookings_by_type?.photoshoot || 0) / (stats.total_bookings || 1) * 100)}%`
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {Math.round(((stats.bookings_by_type?.photoshoot || 0) / (stats.total_bookings || 1) * 100))}% of total
                      </p>
                    </div>
                    {/* Events */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">Events</span>
                        <span className="text-sm font-bold text-gray-900">{stats.bookings_by_type?.event || 0}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div
                          className="h-2.5 rounded-full bg-gradient-to-r from-pink-500 to-pink-600 transition-all duration-500"
                          style={{
                            width: `${((stats.bookings_by_type?.event || 0) / (stats.total_bookings || 1) * 100)}%`
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {Math.round(((stats.bookings_by_type?.event || 0) / (stats.total_bookings || 1) * 100))}% of total
                      </p>
                    </div>
                  </div>
                </div>

                {/* Monthly Revenue Trend */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Monthly Revenue</h3>
                  <div className="flex items-end justify-between space-x-1.5 h-40">
                    {stats.monthly_stats && Object.keys(stats.monthly_stats).length > 0 ? (
                      Object.entries(stats.monthly_stats)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .slice(-6)
                        .map(([month, data]) => {
                          const maxRevenue = Math.max(...Object.values(stats.monthly_stats).map(d => d.revenue));
                          const heightPercent = maxRevenue > 0 ? (data.revenue / maxRevenue) * 100 : 0;
                          return (
                            <div key={month} className="flex-1 flex flex-col items-center group relative">
                              <div
                                className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-md hover:from-emerald-600 hover:to-emerald-500 transition-all cursor-pointer"
                                style={{ height: `${heightPercent}%`, minHeight: '8px' }}
                              ></div>
                              <span className="text-[10px] text-gray-500 mt-2 font-medium">
                                {moment(month).format('MMM')}
                              </span>
                              {/* Tooltip */}
                              <div className="absolute bottom-full mb-2 hidden group-hover:flex flex-col bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg z-10 whitespace-nowrap">
                                <span className="font-semibold">{moment(month).format('MMM YYYY')}</span>
                                <span className="text-emerald-300">₹{data.revenue.toLocaleString()}</span>
                                <span className="text-gray-300">{data.bookings} bookings</span>
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                        No data available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Filters and Actions */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">All Status</option>
                      <option value="confirmed">Confirmed (upcoming)</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>

                    <select
                      value={filters.booking_type}
                      onChange={(e) => setFilters({ ...filters, booking_type: e.target.value, page: 1 })}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">All Types</option>
                      <option value="photoshoot">Photoshoot</option>
                      <option value="event">Event</option>
                    </select>

                    <input
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => setFilters({ ...filters, start_date: e.target.value, page: 1 })}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Start Date"
                    />

                    <input
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => setFilters({ ...filters, end_date: e.target.value, page: 1 })}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="End Date"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowFreeBooking(true)}
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Free Booking
                    </button>

                    <button
                      onClick={() => setShowBlockSlot(true)}
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors"
                    >
                      <Square className="w-4 h-4 mr-2" />
                      Block Slot
                    </button>

                    <button
                      onClick={openExportModal}
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </button>
                  </div>
                </div>
              </div>

              {/* Bookings Table with Pagination */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b" style={{ borderColor: '#F4ECDE' }}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold" style={{ color: '#471F21' }}>📋 All Bookings</h3>
                    <div className="flex items-center gap-4">
                      {ListControls()}
                      <div className="text-sm text-gray-600">
                        {bookings.length > 0
                          ? `Showing ${(filters.page - 1) * filters.limit + 1} - ${(filters.page - 1) * filters.limit + bookings.length} of ${pagination.total ?? bookings.length}`
                          : 'No bookings'}
                      </div>
                    </div>
                  </div>

                  {/* Quick Filter Chips */}
                  <div className="mt-4 flex flex-wrap gap-2 items-center">
                    {/* Reset Button */}
                    {quickFilter.length > 0 && (
                      <button
                        onClick={() => setQuickFilter([])}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all bg-red-100 text-red-700 hover:bg-red-200 border border-red-300"
                      >
                        <span>✕</span>
                        <span>Reset Filters</span>
                      </button>
                    )}

                    {[
                      { id: 'blocked', label: 'Blocked', icon: '🚫', count: bookings.filter(b => b.payment_status === 'blocked').length },
                      { id: '3hr', label: '3hr Slots', icon: '⏱️', count: bookings.filter(b => b.slot_id?.startsWith('A')).length },
                      { id: '6hr', label: '6hr Slots', icon: '⏰', count: bookings.filter(b => b.slot_id?.startsWith('B')).length },
                      { id: '12hr', label: '12hr Slots', icon: '🕐', count: bookings.filter(b => b.slot_id === 'C1').length },
                      { id: 'cancelled', label: 'Cancelled', icon: '✗', count: bookings.filter(b => b.payment_status === 'cancelled').length },
                      { id: 'confirmed', label: 'Confirmed', icon: '✓', count: bookings.filter(b => b.payment_status === 'confirmed').length },
                      { id: 'photoshoot', label: 'Photoshoots', icon: '📸', count: bookings.filter(b => b.booking_type === 'photoshoot').length },
                      { id: 'event', label: 'Events', icon: '🎉', count: bookings.filter(b => b.booking_type === 'event').length }
                    ].map(filter => {
                      const isActive = quickFilter.includes(filter.id);
                      return (
                        <button
                          key={filter.id}
                          onClick={() => {
                            if (isActive) {
                              // Remove filter
                              setQuickFilter(quickFilter.filter(f => f !== filter.id));
                            } else {
                              // Add filter
                              setQuickFilter([...quickFilter, filter.id]);
                            }
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${isActive
                            ? 'bg-[#471F21] text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          <span>{filter.icon}</span>
                          <span>{filter.label}</span>
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isActive
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-200 text-gray-600'
                            }`}>
                            {filter.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead style={{ backgroundColor: '#471F21' }}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          ID Proof
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Guest
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Date & Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        // Ordering and the status/type/date filters are the server's job now.
                        // The quick chips remain a client-side refinement of the loaded page.
                        let rows = filteredBookings;

                        if (quickFilter.length > 0) {
                          rows = rows.filter(b => {
                            return quickFilter.some(filter => {
                              if (filter === 'blocked') return b.payment_status === 'blocked';
                              if (filter === '3hr') return b.slot_id?.startsWith('A');
                              if (filter === '6hr') return b.slot_id?.startsWith('B');
                              if (filter === '12hr') return b.slot_id === 'C1';
                              if (filter === 'cancelled') return b.payment_status === 'cancelled';
                              if (filter === 'confirmed') return b.payment_status === 'confirmed';
                              if (filter === 'photoshoot') return b.booking_type === 'photoshoot';
                              if (filter === 'event') return b.booking_type === 'event';
                              return false;
                            });
                          });
                        }

                        return rows.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                              No bookings found
                            </td>
                          </tr>
                        ) : (
                          rows.map(booking => (
                            <tr
                              key={booking.id}
                              className="hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => viewBookingDetails(booking)}
                            >
                              <td className="px-4 py-4">
                                <div className="text-sm text-gray-900">
                                  {booking.id_proof_url ? (
                                    <a
                                      href={booking.id_proof_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                      View ID
                                    </a>
                                  ) : (
                                    <span className="text-gray-400 text-xs italic">Not uploaded</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div>
                                  <div className="font-medium text-gray-900">{booking.user_name}</div>
                                  <div className="text-xs text-gray-500">{booking.user_email}</div>
                                  <div className="text-xs text-gray-500">{booking.phone}</div>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center">
                                  <span className="text-sm">
                                    {booking.booking_type === 'photoshoot' ? '📸 Photoshoot' : '🎉 Event'}
                                  </span>
                                </div>
                                {booking.shoot_type && (
                                  <div className="text-xs text-gray-500 mt-1">{booking.shoot_type}</div>
                                )}
                              </td>
                              <td className="px-4 py-4">
                                <div className="text-sm text-gray-900">
                                  {moment(booking.slot_time).utcOffset(330).format('DD MMM YYYY')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {moment(booking.slot_time).utcOffset(330).format('hh:mm A')}
                                </div>
                                {booking.duration_label && (
                                  <div className="text-xs text-gray-500 mt-1">{booking.duration_label}</div>
                                )}
                              </td>
                              <td className="px-4 py-4">
                                <span className={`px-3 py-1 text-xs font-bold rounded-full ${booking.payment_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  booking.payment_status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                    booking.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      booking.payment_status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                        booking.payment_status === 'blocked' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                          'bg-yellow-100 text-yellow-800'
                                  }`}>
                                  {booking.payment_status === 'confirmed' ? '✓ Confirmed' :
                                    booking.payment_status === 'completed' ? '✓ Completed' :
                                      booking.payment_status === 'pending' ? '⏳ Pending' :
                                        booking.payment_status === 'cancelled' ? '✗ Cancelled' :
                                          booking.payment_status === 'blocked' ? '🚫 Blocked' :
                                            '⏳ Pending'}
                                </span>
                              </td>
                              <td className="px-4 py-4">
                                <div className="text-sm font-semibold text-gray-900">
                                  ₹{(booking.total_amount || booking.amount || 0).toLocaleString()}
                                </div>
                                {booking.deposit && (
                                  <div className="text-xs text-gray-500">
                                    Deposit: ₹{booking.deposit.toLocaleString()}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      viewBookingDetails(booking);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                  >
                                    View Details
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteBooking(booking.id);
                                    }}
                                    className="text-red-500 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50 transition-colors"
                                    title="Delete Permanently"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {Pager()}
              </div>
            </div>
          )}

          {/* Photoshoots View */}
          {currentView === 'photoshoots' && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <h2 className="text-3xl font-bold" style={{ color: '#471F21' }}>📸 Photoshoot Bookings</h2>
                {ListControls()}
              </div>
              {/* Bookings Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead style={{ backgroundColor: '#471F21' }}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Guest
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Type & Duration
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Event Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Booked On
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Rows arrive filtered and ordered from the server. The old grouped
                          "Cancelled Bookings" separator assumed cancelled rows were always
                          contiguous, which is not true once the list can be sorted by any
                          column — the status badge and dimming already mark them. */}
                      {filteredBookings.map((booking) => {
                        return (
                          <React.Fragment key={booking.id}>
                            <tr className={`hover:bg-gray-50 transition-colors ${booking.payment_status === 'cancelled' ? 'opacity-60 bg-gray-50' : ''}`}>
                              {/* Guest */}
                              <td className="px-4 py-4">
                                <div>
                                  <div className="text-sm font-semibold text-gray-900">{booking.user_name}</div>
                                  <div className="text-xs text-gray-600">{booking.phone}</div>
                                </div>
                              </td>

                              {/* Type & Duration */}
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${booking.booking_type === 'photoshoot'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-blue-100 text-blue-800'
                                    }`}>
                                    {booking.booking_type === 'photoshoot' ? '📸' : '🎉'}
                                  </span>
                                  {booking.slot_id && <SlotBadge slotId={booking.slot_id} />}
                                </div>
                                <div className="text-xs text-gray-700 mt-1">
                                  {booking.slot_id ? getSlotLabel(booking.slot_id) : (booking.duration_label || booking.duration || 'N/A')}
                                </div>
                                {booking.event_type && (
                                  <div className="text-xs text-gray-500">{booking.event_type}</div>
                                )}
                              </td>

                              {/* Event Date */}
                              <td className="px-3 py-3 text-sm">
                                <div className="font-medium text-gray-900">
                                  {moment(booking.slot_time).utcOffset(330).format('DD/MM/YYYY')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {moment(booking.slot_time).utcOffset(330).format('hh:mm A')}
                                </div>
                              </td>

                              {/* Booked On */}
                              <td className="px-3 py-3 text-xs text-gray-600">
                                <div>
                                  {istDate(booking.created_at)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {istTime(booking.created_at)}
                                </div>
                              </td>

                              {/* Amount */}
                              <td className="px-3 py-3">
                                <div className="text-sm font-semibold text-gray-900">
                                  ₹{(booking.total_amount || booking.amount || 0).toLocaleString()}
                                </div>
                                {booking.deposit > 0 && (
                                  <div className="text-xs text-green-600">
                                    +₹{booking.deposit.toLocaleString()}
                                  </div>
                                )}
                              </td>

                              {/* Status */}
                              <td className="px-4 py-4 text-center">
                                <span className={`inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-full ${booking.payment_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  booking.payment_status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                    booking.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      booking.payment_status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                        booking.payment_status === 'failed' ? 'bg-red-100 text-red-800' :
                                          booking.payment_status === 'blocked' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                            'bg-purple-100 text-purple-800'
                                  }`}>
                                  {booking.payment_status === 'confirmed' ? '✓ Confirmed' :
                                    booking.payment_status === 'completed' ? '✓ Completed' :
                                      booking.payment_status === 'pending' ? '⏳ Pending' :
                                        booking.payment_status === 'cancelled' ? '✗ Cancelled' :
                                          booking.payment_status === 'failed' ? '✗ Failed' :
                                            booking.payment_status === 'blocked' ? '🚫 Blocked' :
                                              '❓ ' + booking.payment_status}
                                </span>
                              </td>

                              {/* Actions */}
                              <td className="px-4 py-4">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => viewBookingDetails(booking)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="View Details"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </button>
                                  {booking.payment_status !== 'cancelled' && (
                                    <button
                                      onClick={() => resendWhatsApp(booking)}
                                      disabled={resendingId === booking.id}
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                                      title="Resend confirmation WhatsApp"
                                    >
                                      {resendingId === booking.id ? (
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                      ) : (
                                        <MessageSquare className="w-5 h-5" />
                                      )}
                                    </button>
                                  )}
                                  {booking.payment_status === 'failed' && (
                                    <button
                                      onClick={() => recheckPayment(booking)}
                                      disabled={recheckingId === booking.id}
                                      className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                                      title="Re-check payment with PayU"
                                    >
                                      {recheckingId === booking.id ? (
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                      ) : (
                                        <CreditCard className="w-5 h-5" />
                                      )}
                                    </button>
                                  )}
                                  {['confirmed', 'completed', 'cancelled'].includes(booking.payment_status) && (
                                    <button
                                      onClick={() => resendCaretaker(booking)}
                                      disabled={caretakerResendId === booking.id}
                                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                                      title={`Resend caretaker ${booking.payment_status === 'cancelled' ? 'cancellation' : 'booking'} alert`}
                                    >
                                      {caretakerResendId === booking.id ? (
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                      ) : (
                                        <Bell className="w-5 h-5" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {Pager()}
              </div>

            </div>
          )}

          {/* Events View */}
          {currentView === 'events' && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <h2 className="text-3xl font-bold" style={{ color: '#471F21' }}>🎉 Event Bookings</h2>
                {ListControls()}
              </div>

              {/* Under Development Notice */}
              <div className="mb-6 bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <svg className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-1">Under Development</h3>
                    <p className="text-sm text-yellow-800 mb-2">The legacy event booking system is being phased out. Please use the <strong>Events (Ticketing)</strong> section for new event management.</p>
                    <p className="text-xs text-yellow-700">Existing event bookings are shown below for reference only.</p>
                  </div>
                </div>
              </div>

              {/* Use same table structure but with filtered data */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead style={{ backgroundColor: '#471F21' }}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Guest
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Event Details
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Event Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredBookings.map(booking => (
                        <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4">
                            <div className="text-sm font-semibold">{booking.user_name}</div>
                            <div className="text-xs text-gray-600">{booking.phone}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium">{booking.event_type || 'Event'}</div>
                            <div className="text-xs text-gray-500">Guests: {booking.guest_count || 'N/A'}</div>
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <div className="font-medium">{moment(booking.slot_time).utcOffset(330).format('DD/MM/YYYY')}</div>
                            <div className="text-xs text-gray-500">{moment(booking.slot_time).utcOffset(330).format('hh:mm A')} - {moment(booking.slot_time).utcOffset(330).add(parseFloat(booking.duration), 'hours').format('hh:mm A')}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-semibold">₹{(booking.total_amount || 0).toLocaleString()}</div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className={`px-3 py-1.5 text-xs font-bold rounded-full ${booking.payment_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              booking.payment_status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                booking.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  booking.payment_status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                    booking.payment_status === 'failed' ? 'bg-red-100 text-red-800' :
                                      booking.payment_status === 'blocked' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                        'bg-purple-100 text-purple-800'
                              }`}>
                              {booking.payment_status === 'confirmed' ? '✓ Confirmed' :
                                booking.payment_status === 'completed' ? '✓ Completed' :
                                  booking.payment_status === 'pending' ? '⏳ Pending' :
                                    booking.payment_status === 'cancelled' ? '✗ Cancelled' :
                                      booking.payment_status === 'failed' ? '✗ Failed' :
                                        booking.payment_status === 'blocked' ? '🚫 Blocked' :
                                          '❓ ' + booking.payment_status}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => viewBookingDetails(booking)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="View Details">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>

                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {Pager()}
              </div>
            </div>
          )}

          {/* Events Ticketing View */}
          {currentView === 'events-ticketing' && (
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#471F21' }}>🎟️ Events (Ticketing)</h2>
              <EventsTicketing />
            </div>
          )}

          {/* Completed View */}
          {currentView === 'completed' && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <h2 className="text-3xl font-bold" style={{ color: '#471F21' }}>✅ Completed Bookings</h2>
                <div className="flex flex-wrap items-center gap-4">
                  {ListControls()}
                  {/* Server total, not the size of the loaded page */}
                  <div className="text-sm text-gray-600">Total: {pagination.total ?? 0} completed</div>
                </div>
              </div>

              {/* Completed Bookings Table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead style={{ backgroundColor: '#471F21' }}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Guest
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Type & Details
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Event Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredBookings.map(booking => (
                        <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4">
                            <div className="text-sm font-semibold">{booking.user_name}</div>
                            <div className="text-xs text-gray-600">{booking.phone}</div>
                            <div className="text-xs text-gray-500">{booking.user_email}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {booking.booking_type === 'photoshoot' ? '📸' : '🎉'}
                              </span>
                              {booking.slot_id && <SlotBadge slotId={booking.slot_id} />}
                            </div>
                            <div className="text-sm font-medium mt-1">
                              {booking.booking_type === 'photoshoot' ? 'Photoshoot' : 'Event'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {booking.slot_id ? getSlotLabel(booking.slot_id) : (booking.duration_label || booking.duration)}
                            </div>
                            {booking.event_type && (
                              <div className="text-xs text-gray-500">{booking.event_type}</div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <div className="font-medium">{moment(booking.slot_time).utcOffset(330).format('DD/MM/YYYY')}</div>
                            <div className="text-xs text-gray-500">{moment(booking.slot_time).utcOffset(330).format('hh:mm A')} - {moment(booking.slot_time).utcOffset(330).add(parseFloat(booking.duration), 'hours').format('hh:mm A')}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-semibold">
                              ₹{(booking.total_amount || booking.amount || 0).toLocaleString()}
                            </div>
                            {booking.deposit > 0 && (
                              <div className="text-xs text-green-600">+₹{booking.deposit.toLocaleString()} deposit</div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => viewBookingDetails(booking)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="View Details"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Empty State */}
                {filteredBookings.length === 0 && (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No completed bookings yet</p>
                    <p className="text-gray-400 text-sm mt-2">Completed events will appear here</p>
                  </div>
                )}
                {Pager()}
              </div>
            </div>
          )}

          {/* Cancelled View */}
          {currentView === 'cancelled' && (
            <div>
              <div className="mb-8">
                <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
                  <h2 className="text-3xl font-bold" style={{ color: '#471F21' }}>❌ Cancelled Photoshoots</h2>
                  {ListControls()}
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead style={{ backgroundColor: '#471F21' }}>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Guest</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Type & Details</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Event Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredBookings.filter(b => (b.booking_type || 'photoshoot') === 'photoshoot').map(booking => (
                          <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4">
                              <div className="text-sm font-semibold">{booking.user_name}</div>
                              <div className="text-xs text-gray-600">{booking.phone}</div>
                              <div className="text-xs text-gray-500">{booking.user_email}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg">📸</span>
                                {booking.slot_id && <SlotBadge slotId={booking.slot_id} />}
                              </div>
                              <div className="text-sm font-medium">Photoshoot</div>
                              <div className="text-xs text-gray-500">
                                {booking.slot_id ? getSlotLabel(booking.slot_id) : (booking.duration_label || booking.duration || 'N/A')}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm">
                              <div className="font-medium">{moment(booking.slot_time).utcOffset(330).format('DD/MM/YYYY')}</div>
                              <div className="text-xs text-gray-500">{moment(booking.slot_time).utcOffset(330).format('hh:mm A')} - {moment(booking.slot_time).utcOffset(330).add(parseFloat(booking.duration), 'hours').format('hh:mm A')}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm font-semibold">₹{(booking.total_amount || booking.amount || 0).toLocaleString()}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center justify-center gap-2">
                                <button onClick={() => viewBookingDetails(booking)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="View Details">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl font-bold mb-4" style={{ color: '#471F21' }}>❌ Cancelled Events</h2>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead style={{ backgroundColor: '#471F21' }}>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Guest</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Event Details</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Event Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredBookings.filter(b => b.booking_type === 'event').map(booking => (
                          <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4">
                              <div className="text-sm font-semibold">{booking.user_name}</div>
                              <div className="text-xs text-gray-600">{booking.phone}</div>
                              <div className="text-xs text-gray-500">{booking.user_email}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm font-medium">{booking.event_type || 'Event'}</div>
                              {booking.guest_count !== undefined && (
                                <div className="text-xs text-gray-500">Guests: {booking.guest_count}</div>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm">
                              <div className="font-medium">{moment(booking.slot_time).utcOffset(330).format('DD/MM/YYYY')}</div>
                              <div className="text-xs text-gray-500">{moment(booking.slot_time).utcOffset(330).format('hh:mm A')}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm font-semibold">₹{(booking.total_amount || booking.amount || 0).toLocaleString()}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center justify-center gap-2">
                                <button onClick={() => viewBookingDetails(booking)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="View Details">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {Pager()}
                </div>
              </div>
            </div>
          )}

          {/* Settings View */}
          {currentView === 'settings' && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h2 className="text-3xl font-bold" style={{ color: '#471F21' }}>⚙️ Settings</h2>

                {/* Tab Navigation */}
                <div className="flex flex-wrap items-center bg-white p-1 rounded-xl shadow-sm border border-gray-100">
                  {[
                    { id: 'general', label: 'General', icon: Settings },
                    { id: 'notices', label: 'Shoot Notices', icon: Bell },
                    { id: 'photoshoot', label: 'Photoshoots', icon: Camera },
                    { id: 'event', label: 'Event Pricing', icon: DollarSign },
                    { id: 'notifications', label: 'Notifications', icon: RefreshCw },
                    { id: 'policies', label: 'Policies', icon: Shield }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setSettingsTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${settingsTab === tab.id
                        ? 'bg-[#471F21] text-white shadow-md'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      <tab.icon className={`w-4 h-4 ${settingsTab === tab.id ? 'text-white' : 'text-gray-400'}`} />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="min-h-[500px]">
                {/* General Settings */}
                {settingsTab === 'general' && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <SettingsSiteBanner />
                    <SettingsPhotoshootPricing />
                    <SettingsBookingWindow />
                    {/* Placeholder for other global settings if needed */}
                  </div>
                )}

                {/* Shoot Notices & Broadcasts */}
                {settingsTab === 'notices' && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <SettingsShootNotices />
                  </div>
                )}

                {/* Photoshoot Slots */}
                {settingsTab === 'photoshoot' && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-gray-800">Photoshoot Slots</h3>
                      <p className="text-sm text-gray-500 mt-1">Manage availability, pricing, and timing for photoshoot bookings.</p>
                    </div>
                    <SettingsPhotoshootSlots />
                  </div>
                )}

                {/* Event Pricing */}
                {settingsTab === 'event' && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-gray-800">Event Quote Configuration</h3>
                      <p className="text-sm text-gray-500 mt-1">Configure baseline costs and multipliers for automated event quotes.</p>
                    </div>
                    <SettingsEventQuote />
                  </div>
                )}

                {/* Notifications */}
                {settingsTab === 'notifications' && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <SettingsNotifications />
                  </div>
                )}

                {/* Policies */}
                {settingsTab === 'policies' && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <SettingsTerms />
                    <SettingsPolicies />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Website Content View */}
          {currentView === 'website' && (
            <div>
              <LandingPageEditor />
            </div>
          )}

          {/* Calendar Manager View */}
          {currentView === 'calendar' && (
            <div className="animate-in fade-in zoom-in-95 duration-200">
              <CalendarManager isAuthenticated={isAuthenticated} />
            </div>
          )}

          {/* Blocked Slots View */}
          {currentView === 'blocked-slots' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">Blocked Slots</h2>
                <div className="flex items-center gap-2">
                  {selectedSlotIds.size > 0 && (
                    <button
                      onClick={bulkUnblockSlots}
                      disabled={loading}
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      <Unlock className="w-4 h-4 mr-2" />
                      Unblock Selected ({selectedSlotIds.size})
                    </button>
                  )}
                  <button
                    onClick={() => setShowBlockSlot(true)}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Block New Slot
                  </button>
                </div>
              </div>

              {/* Filter Bar */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="date"
                    value={blockedSlotsFilters.start_date}
                    onChange={(e) => setBlockedSlotsFilters({ ...blockedSlotsFilters, start_date: e.target.value, page: 1 })}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Start Date"
                  />
                  <input
                    type="date"
                    value={blockedSlotsFilters.end_date}
                    onChange={(e) => setBlockedSlotsFilters({ ...blockedSlotsFilters, end_date: e.target.value, page: 1 })}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="End Date"
                  />
                  <select
                    value={blockedSlotsFilters.slot_id}
                    onChange={(e) => setBlockedSlotsFilters({ ...blockedSlotsFilters, slot_id: e.target.value, page: 1 })}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Slots</option>
                    <option value="A1">A1 (06:30 AM - 3hr)</option>
                    <option value="A2">A2 (11:00 AM - 3hr)</option>
                    <option value="A3">A3 (03:30 PM - 3hr)</option>
                    <option value="B1">B1 (06:00 AM - 6hr)</option>
                    <option value="B2">B2 (01:00 PM - 6hr)</option>
                    <option value="C1">C1 (06:00 AM - 12hr)</option>
                  </select>

                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">Sort by</span>
                  <select
                    value={blockedSlotsFilters.sort_by}
                    onChange={(e) => setBlockedSlotsFilters({ ...blockedSlotsFilters, sort_by: e.target.value, page: 1 })}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="date">Blocked date</option>
                    <option value="slot">Slot</option>
                    <option value="reason">Reason</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setBlockedSlotsFilters({ ...blockedSlotsFilters, sort_dir: blockedSlotsFilters.sort_dir === 'asc' ? 'desc' : 'asc', page: 1 })}
                    title={blockedSlotsFilters.sort_dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                    className="px-2.5 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {blockedSlotsFilters.sort_dir === 'asc' ? '↑' : '↓'}
                  </button>
                  <select
                    value={blockedSlotsFilters.limit}
                    onChange={(e) => setBlockedSlotsFilters({ ...blockedSlotsFilters, limit: parseInt(e.target.value, 10), page: 1 })}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                  </select>

                  <button
                    onClick={() => setBlockedSlotsFilters({ start_date: '', end_date: '', slot_id: '', sort_by: 'date', sort_dir: 'desc', page: 1, limit: 25 })}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {blockedSlots.length === 0 ? (
                  <div className="p-12 text-center">
                    <Shield className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg">No blocked slots found</p>
                    <p className="text-gray-400 text-sm mt-2">Try adjusting your filters or block a new slot</p>
                  </div>
                ) : (
                  <>
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              checked={blockedSlots.length > 0 && selectedSlotIds.size === blockedSlots.length}
                              onChange={(e) => setSelectedSlotIds(e.target.checked ? new Set(blockedSlots.map(s => s.id)) : new Set())}
                              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slot</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Blocked On</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {blockedSlots.map((slot) => {
                          const slotDetails = {
                            'A1': { label: '06:30 AM', duration: '3hr', color: 'bg-purple-100 text-purple-800' },
                            'A2': { label: '11:00 AM', duration: '3hr', color: 'bg-purple-100 text-purple-800' },
                            'A3': { label: '03:30 PM', duration: '3hr', color: 'bg-purple-100 text-purple-800' },
                            'B1': { label: '06:00 AM', duration: '6hr', color: 'bg-blue-100 text-blue-800' },
                            'B2': { label: '01:00 PM', duration: '6hr', color: 'bg-blue-100 text-blue-800' },
                            'C1': { label: '06:00 AM', duration: '12hr', color: 'bg-green-100 text-green-800' }
                          }[slot.slot_id] || { label: 'Unknown', duration: '', color: 'bg-gray-100 text-gray-800' };

                          return (
                            <tr key={slot.id} className={`hover:bg-gray-50 ${selectedSlotIds.has(slot.id) ? 'bg-red-50' : ''}`}>
                              <td className="px-4 py-4 w-10">
                                <input
                                  type="checkbox"
                                  checked={selectedSlotIds.has(slot.id)}
                                  onChange={(e) => {
                                    const next = new Set(selectedSlotIds);
                                    e.target.checked ? next.add(slot.id) : next.delete(slot.id);
                                    setSelectedSlotIds(next);
                                  }}
                                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {moment(slot.slot_time).utcOffset(330).format('MMM DD, YYYY')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {moment(slot.slot_time).utcOffset(330).format('dddd')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${slotDetails.color}`}>
                                  {slot.slot_id} · {slotDetails.label} ({slotDetails.duration})
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900 max-w-xs truncate">
                                  {slot.reason || <span className="text-gray-400 italic">No reason provided</span>}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {moment(slot.created_at).utcOffset(330).format('MMM DD, YYYY h:mm A')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => unblockSlot(slot.id)}
                                  className="inline-flex items-center px-3 py-1.5 text-red-600 hover:text-red-900 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Unblock this slot"
                                >
                                  <Unlock className="w-4 h-4 mr-1" />
                                  Unblock
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {blockedSlotsPagination.total_pages > 1 && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Showing <span className="font-medium">{((blockedSlotsPagination.page - 1) * blockedSlotsPagination.limit) + 1}</span> to{' '}
                          <span className="font-medium">
                            {Math.min(blockedSlotsPagination.page * blockedSlotsPagination.limit, blockedSlotsPagination.total)}
                          </span> of{' '}
                          <span className="font-medium">{blockedSlotsPagination.total}</span> results
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setBlockedSlotsFilters({ ...blockedSlotsFilters, page: blockedSlotsFilters.page - 1 })}
                            disabled={blockedSlotsFilters.page === 1}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <span className="px-4 py-1.5 text-sm font-medium text-gray-700">
                            Page {blockedSlotsPagination.page} of {blockedSlotsPagination.total_pages}
                          </span>
                          <button
                            onClick={() => setBlockedSlotsFilters({ ...blockedSlotsFilters, page: blockedSlotsFilters.page + 1 })}
                            disabled={blockedSlotsFilters.page >= blockedSlotsPagination.total_pages}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

        </div>
      </div >

      {/* Free Booking Modal */}
      {
        showFreeBooking && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create Free Booking</h3>
                <form onSubmit={createFreeBooking} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input
                        type="text"
                        name="user_name"
                        value={freeBookingData.user_name}
                        onChange={handleFreeBookingChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        name="user_email"
                        value={freeBookingData.user_email}
                        onChange={handleFreeBookingChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={freeBookingData.phone}
                        onChange={handleFreeBookingChange}
                        required
                        pattern="[0-9]{10}"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Booking Type *</label>
                      <select
                        name="booking_type"
                        value={freeBookingData.booking_type}
                        onChange={handleFreeBookingChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="photoshoot">Photoshoot</option>
                        <option value="event">Event</option>
                      </select>
                    </div>

                    {freeBookingData.booking_type === 'photoshoot' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Shoot Type</label>
                        <select
                          name="shoot_type"
                          value={freeBookingData.shoot_type}
                          onChange={handleFreeBookingChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">Select Type</option>
                          <option value="Pre-wedding">Pre-wedding</option>
                          <option value="Maternity">Maternity</option>
                          <option value="Baby Shoot">Baby Shoot</option>
                          <option value="Fashion/Model">Fashion/Model</option>
                          <option value="Family Portrait">Family Portrait</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                        <select
                          name="event_type"
                          value={freeBookingData.event_type}
                          onChange={handleFreeBookingChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="">Select Type</option>
                          <option value="Birthday">Birthday</option>
                          <option value="Anniversary">Anniversary</option>
                          <option value="Engagement">Engagement</option>
                          <option value="Corporate">Corporate</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Guest Count</label>
                      <input
                        type="number"
                        name="guest_count"
                        value={freeBookingData.guest_count}
                        onChange={handleFreeBookingChange}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Number of people"
                      />
                    </div>

                    {/* Date & Slot Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                      <input
                        type="date"
                        name="date"
                        value={freeBookingData.date}
                        onChange={handleFreeBookingChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Slot *</label>
                      <DynamicSlotPicker
                        selectedDate={freeBookingData.date}
                        selectedSlot={freeBookingData.slot_id}
                        onSelect={(id) => setFreeBookingData(prev => ({ ...prev, slot_id: id }))}
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Special Requirements</label>
                      <textarea
                        name="special_requirements"
                        value={freeBookingData.special_requirements}
                        onChange={handleFreeBookingChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                      style={{ backgroundColor: '#471F21' }}
                    >
                      {loading ? 'Creating...' : 'Create Free Booking'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowFreeBooking(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Cancel Booking Modal */}
      {
        showCancelModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Cancel Booking</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Reason *</label>
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      required
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="Customer request, venue issue, etc..."
                    />
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-sm text-red-800">
                      ⚠️ This action cannot be undone. The slot will be freed for other bookings.
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={cancelBooking}
                      disabled={loading}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Cancelling...' : 'Confirm Cancel'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCancelModal(false);
                        setCancelReason('');
                      }}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Bookings Modal */}
      {
        showExportModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium mb-1" style={{ color: '#471F21' }}>Export Bookings</h3>
                <p className="text-sm text-gray-500 mb-5">
                  Downloads every booking whose slot date falls in the range — not just this page.
                </p>

                {/* Quick presets */}
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {exportPresets.map((preset) => {
                    const range = preset.range();
                    const active = exportRange.start_date === range.start_date
                      && exportRange.end_date === range.end_date;
                    return (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => setExportRange({ ...exportRange, ...range })}
                        className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                          active
                            ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                            : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <span className="block font-medium">{preset.label}</span>
                        <span className="block text-xs text-gray-500">{preset.hint}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From date</label>
                      <input
                        type="date"
                        value={exportRange.start_date}
                        onChange={(e) => setExportRange({ ...exportRange, start_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">To date</label>
                      <input
                        type="date"
                        value={exportRange.end_date}
                        min={exportRange.start_date || undefined}
                        onChange={(e) => setExportRange({ ...exportRange, end_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 -mt-2">
                    Leave a date blank for open-ended. Both blank exports everything.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={exportRange.status}
                      onChange={(e) => setExportRange({ ...exportRange, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white"
                    >
                      <option value="">All statuses</option>
                      <option value="confirmed">Confirmed only</option>
                      <option value="completed">Completed only</option>
                      <option value="pending">Pending only</option>
                      <option value="cancelled">Cancelled only</option>
                      <option value="failed">Failed only</option>
                    </select>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">
                      CSV opens directly in Excel. Contains guest contact details — handle accordingly.
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={exportBookings}
                      disabled={exporting}
                      className="flex-1 flex items-center justify-center bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {exporting ? 'Preparing...' : 'Download CSV'}
                    </button>
                    <button
                      onClick={() => setShowExportModal(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Booking Details Modal */}
      {
        showBookingDetails && selectedBooking && (
          <BookingDetailsModal
            booking={selectedBooking}
            onClose={() => setShowBookingDetails(false)}
            onUpdated={() => { loadBookings(); loadBlockedSlots(); }}
          />
        )
      }

      {/* Block Slot Modal */}
      {
        showBlockSlot && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium mb-4" style={{ color: '#471F21' }}>Block Time Slot</h3>

                {/* Type Toggle */}
                <div className="flex mb-5 border-b">
                  <button
                    className={`flex-1 pb-2 text-sm font-semibold ${blockSlotType === 'slot' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => setBlockSlotType('slot')}
                  >
                    Single Slot
                  </button>
                  <button
                    className={`flex-1 pb-2 text-sm font-semibold ${blockSlotType === 'range' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => setBlockSlotType('range')}
                  >
                    Date Range (All Slots)
                  </button>
                </div>

                <form onSubmit={blockSlot} className="space-y-4">

                  {blockSlotType === 'slot' ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                          Select Date *
                        </label>
                        <DatePicker
                          selected={blockSlotDate}
                          onChange={(date) => setBlockSlotDate(date)}
                          minDate={new Date()}
                          maxDate={new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)}
                          dateFormat="MMMM d, yyyy"
                          placeholderText="Choose a date"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                          Select Slot *
                        </label>
                        <DynamicSlotPicker
                          selectedDate={blockSlotDate}
                          selectedSlot={blockSlotId}
                          onSelect={setBlockSlotId}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                          Start Date *
                        </label>
                        <DatePicker
                          selected={blockSlotStartDate}
                          onChange={(date) => setBlockSlotStartDate(date)}
                          minDate={new Date()}
                          dateFormat="MMMM d, yyyy"
                          placeholderText="Start Date"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                          End Date *
                        </label>
                        <DatePicker
                          selected={blockSlotEndDate}
                          onChange={(date) => setBlockSlotEndDate(date)}
                          minDate={blockSlotStartDate || new Date()}
                          dateFormat="MMMM d, yyyy"
                          placeholderText="End Date"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                          required
                        />
                      </div>
                      <p className="col-span-2 text-xs text-red-500 bg-red-50 p-2 rounded">
                        ⚠️ This will block ALL slots (A1-C1) for every day in this range.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#471F21' }}>
                      Reason (Optional)
                    </label>
                    <input
                      type="text"
                      value={blockSlotReason}
                      onChange={(e) => setBlockSlotReason(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none"
                      placeholder="Maintenance, private event, etc."
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: '#471F21' }}
                      onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#8B4446')}
                      onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#471F21')}
                    >
                      {loading && (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {loading ? 'Blocking...' : 'Block Slot'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBlockSlot(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default AdminDashboard;
